import { useEffect, useMemo, useState } from "react";
import { Alert, Spin, Tag, Tooltip, Typography, theme } from "antd";
import { getVariables } from "../repository/data.repository";
import { Variable, VariableType } from "../repository/data.default";
import { retry } from "../utils/http";

export interface TemplateTokenPaletteProps {
  /** Insert the chosen variable as a `{Tag}` token (into the Body field). */
  readonly onInsert: (token: string) => void;
  /** Body + Subject text, scanned to flag `{tokens}` that don't match any known variable. */
  readonly scanText?: string;
}

const TOKEN_REGEX = /\{(.*?)\}/g;

/**
 * Authoring aid for the template editor: a click-to-insert palette of the available
 * variables (grouped System / Custom, sourced live from TemplatesVariables) plus a hint
 * listing any `{token}` typed into the template that doesn't match a known variable —
 * complementing the backend's strip-unresolved-tokens behaviour at render time.
 */
function TemplateTokenPalette(props: TemplateTokenPaletteProps) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [variables, setVariables] = useState<Variable[]>([]);

  useEffect(() => {
    let active = true;
    retry(() =>
      getVariables({
        searchQuery: { conditions: [] },
        pageNumber: 0,
        pageSize: 200,
      }),
    ).then(({ data }) => {
      if (active) {
        setVariables(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const system = variables.filter((v) => v.variableType === VariableType.System);
  const custom = variables.filter((v) => v.variableType === VariableType.Custom);

  const unknownTokens = useMemo(() => {
    if (!props.scanText) return [];
    const known = new Set(variables.map((v) => v.tag));
    const found = new Set<string>();
    const regex = new RegExp(TOKEN_REGEX);
    let match: RegExpExecArray | null;
    while ((match = regex.exec(props.scanText)) !== null) {
      const tag = match[1]?.trim();
      if (tag && !known.has(tag)) found.add(tag);
    }
    return Array.from(found);
  }, [props.scanText, variables]);

  const renderGroup = (label: string, items: Variable[]) =>
    items.length ? (
      <div className="mb-1">
        <Typography.Text
          className="text-xs"
          style={{ color: token.colorTextDescription }}
        >
          {label}
        </Typography.Text>
        <div className="mt-1 flex flex-row flex-wrap gap-1">
          {items.map((v) => (
            <Tooltip key={v.uid} title={v.description}>
              <Tag
                className="m-0 cursor-pointer"
                color="processing"
                onClick={() => props.onInsert(`{${v.tag}}`)}
              >
                {v.tag}
              </Tag>
            </Tooltip>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className="mb-2 mt-1 p-1">
      <Typography.Text className="text-sm" style={{ color: token.colorTextDescription }}>
        Insert variable (appended to Body)
      </Typography.Text>
      {loading ? (
        <div className="mt-1">
          <Spin size="small" />
        </div>
      ) : (
        <div className="mt-1">
          {renderGroup("System", system)}
          {renderGroup("Custom", custom)}
          {!variables.length && (
            <Typography.Text type="secondary" className="text-xs">
              No variables defined yet.
            </Typography.Text>
          )}
        </div>
      )}
      {unknownTokens.length > 0 && (
        <Alert
          className="mt-2"
          type="warning"
          showIcon
          message={
            <span className="text-xs">
              Unknown token{unknownTokens.length > 1 ? "s" : ""}:{" "}
              {unknownTokens.map((t) => `{${t}}`).join(", ")} — these won't resolve and
              will be removed when sent.
            </span>
          }
        />
      )}
    </div>
  );
}

export default TemplateTokenPalette;
